
import { ArticleWithEmbedding } from '@/types/recommendationTypes';
import { ArticleRecommendation } from '@/types/articleTypes';
import { logger } from '@/utils/logger';
import { createOpenAILogError } from '@/utils/loggerTypes';

/**
 * Gets recommendations using the OpenAI Chat Completions API
 */
export async function getRecommendationsWithChatCompletions(
  apiKey: string,
  model: string,
  searchUrl: string,
  searchContent: string,
  searchTitle: string,
  availableArticles: ArticleWithEmbedding[],
  additionalContext?: string,
  isMoreRecommendations?: boolean
): Promise<ArticleRecommendation['recommendations']> {
  try {
    console.log('Getting recommendations with Chat Completions API');
    
    if (availableArticles.length === 0) {
      throw new Error('No articles available for recommendations');
    }
    
    // Prepare site articles data for the API
    const siteArticlesData = availableArticles.map(article => ({
      url: article.url,
      title: article.title,
      snippet: article.content.substring(0, 300) + '...',
      keywords: article.keywords || [],
      // Determine if this is a blog article by analyzing the URL
      isBlogPost: article.url.includes('/blog/') || 
                 article.url.toLowerCase().includes('blog') || 
                 article.url.match(/\d{4}\/\d{2}\//) !== null // Date pattern often used in blog URLs
    }));
    
    console.log(`Prepared ${siteArticlesData.length} articles for OpenAI request`);
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    };
    
    // Enhanced system prompt that emphasizes context importance
    const systemPrompt = `
You are an AI assistant specialized in recommending related articles for a given blog post. Analyze the topic of a provided URL or a brief summary of an article, and suggest five relevant articles from a given sitemap or list of articles.

You will receive information about a source article and a list of candidate related articles. Your task is to:

1. Analyze the source article information (title, URL, and content when available)
2. Evaluate each candidate article based on its relevance to the source
3. Select the 3-5 most relevant articles that would provide valuable additional information to someone reading the source article
4. IMPORTANT: You MUST follow any additional context instructions provided by the user (e.g., "only recommend blog articles", "focus on technical content", etc.)
5. Provide a brief explanation for why each selected article is relevant

# Output Format

Return a JSON object with:
1. An array of recommendations, each containing:
   - articleUrl: The URL of the recommended article
   - articleTitle: The title of the recommended article
   - similarity: A relevance score between 0-1
   - reasoning: Brief explanation of why this article is relevant

IMPORTANT: Your response MUST be valid JSON. Do not include any text outside of the JSON structure.
`;
    
    // Prepare the content
    const contentSummary = searchContent 
        ? `Content summary: ${searchContent.substring(0, 500)}...` 
        : 'Content not available';
        
    // Build the user message with complete site data
    let userMessage = `
Source Article:
- Title: ${searchTitle}
- URL: ${searchUrl}
- ${contentSummary}
`;

    // Add additional context with clear emphasis if provided
    if (additionalContext && additionalContext.trim().length > 0) {
      userMessage += `\n## CRITICAL USER INSTRUCTIONS ##\n${additionalContext.trim()}\n## END USER INSTRUCTIONS ##\n\nYou MUST follow these instructions when making your recommendations.\n`;
    }

    // Add a note if we're requesting more recommendations
    if (isMoreRecommendations) {
      userMessage += `\nNote: Please provide 5 more recommendations that are different from previous suggestions.\n`;
    }

    userMessage += `
Available Articles on the site (${siteArticlesData.length}):
${siteArticlesData.map((article, i) => 
  `${i+1}. Title: "${article.title}"
   URL: ${article.url}
   Content Snippet: ${article.snippet}
   Keywords: ${article.keywords?.join(', ') || 'None'}
   Is Blog Post: ${article.isBlogPost ? 'Yes' : 'No'}`
).join('\n\n')}

Please analyze these articles and select the 3-5 most relevant ones that would be valuable for readers of the source article.
${additionalContext ? `REMEMBER: You must follow the user instructions: "${additionalContext}"` : ''}
`;
    
    console.log(`------ OPENAI REQUEST PAYLOAD ------`);
    console.log(`Model: ${model}`);
    console.log(`Source URL: ${searchUrl}`);
    console.log(`Source Title: ${searchTitle}`);
    console.log(`Content Length: ${searchContent ? searchContent.length : 0} characters`);
    console.log(`Available Articles: ${siteArticlesData.length}`);
    console.log(`System Prompt Length: ${systemPrompt.length} characters`);
    console.log(`User Message Length: ${userMessage.length} characters`);
    console.log(`Additional Context: ${additionalContext || 'None'}`);
    console.log(`Requesting More: ${isMoreRecommendations ? 'Yes' : 'No'}`);
    console.log(`------ END REQUEST PAYLOAD ------`);
    
    // Base request payload with temperature lowered for more precise following of instructions
    const requestPayload = {
      model: model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userMessage }
      ],
      temperature: additionalContext ? 0.2 : 0.3, // Lower temperature when context is provided for stricter adherence
      max_tokens: 2000,
      response_format: { type: "json_object" }
    };
    
    console.log(`Sending request to OpenAI with ${siteArticlesData.length} articles`);
    
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers,
      body: JSON.stringify(requestPayload)
    });
    
    const responseText = await response.text();
    console.log(`------ OPENAI RAW RESPONSE TEXT ------`);
    console.log(responseText);
    console.log(`------ END OPENAI RAW RESPONSE TEXT ------`);
    
    if (!response.ok) {
      console.error('Error response from OpenAI:', responseText);
      throw new Error(`OpenAI API error: ${response.status} ${response.statusText}`);
    }
    
    // Parse the response back to JSON
    const data = JSON.parse(responseText);
    console.log('Successfully received chat completion response');
    
    // Parse the response content
    const content = data.choices?.[0]?.message?.content;
    
    console.log(`------ OPENAI PARSED RESPONSE ------`);
    console.log(content);
    console.log(`------ END OPENAI PARSED RESPONSE ------`);
    
    if (!content) {
      throw new Error('No content in OpenAI response');
    }
    
    // Parse JSON response - look for JSON object within the content if it's not pure JSON
    try {
      // First look for JSON object within the content if it's not pure JSON
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      const jsonContent = jsonMatch ? jsonMatch[0] : content;
      
      const parsedResponse = JSON.parse(jsonContent);
      
      console.log(`------ PARSED RESPONSE ------`);
      console.log(JSON.stringify(parsedResponse, null, 2));
      console.log(`------ END PARSED RESPONSE ------`);
      
      if (!parsedResponse.recommendations || !Array.isArray(parsedResponse.recommendations)) {
        throw new Error('Invalid response format: missing recommendations array');
      }
      
      console.log(`Received ${parsedResponse.recommendations.length} recommendations from OpenAI`);
      
      // Map recommendations to our expected format and add context-based reasoning
      const processedRecommendations = parsedResponse.recommendations.map(rec => ({
        articleUrl: rec.articleUrl,
        articleTitle: rec.articleTitle,
        similarity: rec.similarity || 0.8,
        keywords: rec.keywords || [],
        reasoning: rec.reasoning || `Similar content to the source article`
      }));
      
      // Add a log to verify if context was applied
      if (additionalContext) {
        console.log(`Context '${additionalContext}' was applied to recommendations`);
        
        // Additional logging to help debug context application
        const contextKeywords = additionalContext.toLowerCase().split(/\s+/);
        const matchesContext = processedRecommendations.filter(rec => {
          const isMatchingContext = contextKeywords.some(keyword => 
            rec.articleUrl.toLowerCase().includes(keyword) || 
            rec.reasoning.toLowerCase().includes(keyword)
          );
          return isMatchingContext;
        });
        
        console.log(`${matchesContext.length} of ${processedRecommendations.length} recommendations appear to match the context criteria`);
      }
      
      console.log(`------ FINAL PROCESSED RECOMMENDATIONS ------`);
      console.log(JSON.stringify(processedRecommendations, null, 2));
      console.log(`------ END PROCESSED RECOMMENDATIONS ------`);
      
      return processedRecommendations;
    } catch (parseError) {
      console.error('Failed to parse OpenAI response as JSON:', content);
      throw new Error('Invalid JSON response from OpenAI');
    }
  } catch (error) {
    console.error('Error in Chat Completions API recommendation:', error);
    logger.error('Failed to get recommendations with Chat Completions', 
      createOpenAILogError((error as Error).message, {
        operation: 'chat',
        model: model,
        component: 'ChatCompletionsService'
      })
    );
    throw error;
  }
}
